import React from 'react';
import {
  AGGREGATED_RESERVATION_PAYMENTS_STATUSES,
  CURRENCIES,
  PAYMENT_PROVIDERS,
  RESERVATION_PAYMENT_TYPES,
} from './constants';
import {LocationStateTaxes} from '../components/Taxes/TouristTaxesSetup/TouristTaxesSetup';
import {IdentityLocationStateType} from '../views/VerifyModalScreen/VerifyModalScreen';

export type valueof<T> = T[keyof T];
export type InputEventType = React.ChangeEvent<HTMLInputElement>;
export type FormEventType = React.FormEvent<HTMLFormElement>;
export type SelectOptionType = {
  value: string | number;
  label: string | JSX.Element | React.ReactNode;
};
export type Country = {
  name: string;
  code: string;
  alpha_3: string;
};
export type Status = 'loading' | 'success' | 'error' | '';
export type DatepickerDateType = Date | number | null | undefined;

export type StatusDetails = {
  code: string;
  details: string;
  label: string;
  meaning: string;
};

export enum GUEST_STATUSES {
  incomplete = 'INCOMPLETE',
  error = 'ERROR',
  new = 'NEW',
  complete = 'COMPLETE',
}

export type Guest = {
  arrived_from: string;
  biomatch_doc: string | null;
  biomatch_passed: boolean;
  biomatch_selfie: string | null;
  birth_date: string;
  birth_place: string;
  citizenship: Country;
  created_at: string;
  document: any;
  documents: any[];
  email: string;
  full_name: string;
  gender: string;
  id: string;
  ifa_status: string;
  is_use_in_contract: boolean;
  language: string;
  name: string;
  names: {name: string; surname: string};
  nationality: Country;
  next_destination: string;
  origin: string;
  phone: string;
  phone_details: {code: string; number: string};
  purpose_of_stay: string;
  residence: string;
  room: any;
  second_surname: string;
  signature: string | null;
  status: GUEST_STATUSES;
  status_display: string;
  statuses: {
    police: {
      in: StatusDetails;
      out: StatusDetails;
    };
    statistics: {
      in: StatusDetails;
      out: StatusDetails;
    };
    data: StatusDetails;
    arrival: StatusDetails;
  };
  surname: string;
  tax_exemption: string;
  fiscal_code: string;
  full_tourist_tax: number | null;
};

export type SeasonRule = {
  id: string;
  less_than: null | number;
  price: number;
  season_id: string;
  up_to: null | number;
};

export type Exemption = {
  exemption_type: string;
  id: string;
  price: number;
  season_id: string;
};

export type SeasonLink = {
  housing_id: string;
  id: string;
  season_id: string;
};

export type Season = {
  exemptions: Exemption[];
  price_per_night: number;
  from_date: string;
  housings: string[];
  id: string;
  is_max_nights_taxed: boolean;
  max_nights: number | null;
  max_nights_rate: number | null;
  name: string;
  rules: SeasonRule[];
  season_links: SeasonLink[];
  to_date: string;
};

enum CREDITOR_TYPES {
  individual = 'INDIVIDUAL',
  freelance = 'FREELANCE',
  company = 'COMPANY',
}

export type PaymentDocument = {
  back_side_scan: null | string;
  expiration_date: null | string;
  front_side_scan: string;
  guest: null | string;
  id: string;
  issue_date: null | string;
  issue_place: {
    address: string;
    city: string;
    community: string;
    comune: string;
    country: string;
    district: string;
    division_level_1: string;
    division_level_2: string;
    division_level_3: string;
    municipality: string;
    postal_code: string;
    province: string;
    region: string;
  };
  number: string;
  type: string;
  url: string;
};

export enum PAYMENTS_SETTINGS_STATUSES {
  new = 'NEW',
  validating = 'VALIDATING',
  valid = 'VALID',
  invalid = 'INVALID',
}

export type PaymentsSettings = {
  balance: number;
  company_name: string;
  external_account_id: string;
  creditor_type: CREDITOR_TYPES;
  documents: PaymentDocument[];
  iban: string;
  id: string;
  provider: PAYMENT_PROVIDERS;
  status: PAYMENTS_SETTINGS_STATUSES;
  status_details: string;
  swift: string;
  currency: CURRENCIES;
};

export type LanguageOption = SelectOptionType & {icon: string};

export type ReservationSource = {
  id: string;
  name: string;
};

export type HousingExemption = Readonly<{
  id: string;
  tax_exempt_sources_nested: ReservationSource[];
  booking_exempt_sources_nested: ReservationSource[];
  deposit_exempt_sources_nested: ReservationSource[];
  extra_service_exempt_sources_nested: ReservationSource[];
}>;

export type GuestPaymentAccount = {
  external_id: string;
  id: string;
  provider: 'PAYCOMET';
  reservation_id: string;
  status: 'NEW' | 'CONFIRMED' | 'REJECTED';
  status_details: string;
  temporary_token: string;
  token: string;
};

type SecurityDeposit = {
  id: string;
  charged_amount: number;
  status: 'NEW' | 'CANCELED' | 'RELEASED' | 'CONFIRMED';
  amount: string;
  auth_code: string;
  guest_payment_account: GuestPaymentAccount;
  guest_payment_account_id: string;
  ip_address: string;
};

type PaymentRefund = {
  id: string;
  status: 'NEW' | 'CONFIRMED' | 'PARTIALLY' | 'REJECTED';
  requested_amount: string;
  approved_amount: string;
  status_details: string;
  ip_address: string;
  reservation_id: string;
};

export type GuestGroup = {
  id: string;
  leader_id: string;
  type: string;
  members: Guest[];
  number_of_guests: number;
};

export type ReservationPayment = {
  amount: string;
  amount_with_commission: number;
  app_commission_amount: number;
  guest_commission_amount: number;
  guest_payment_id: null | string;
  id: string;
  managers_profit: number;
  name: string;
  reservation_id: string;
  status: string;
  status_details: string;
  type: RESERVATION_PAYMENT_TYPES;
};

export type Reservation = {
  is_security_deposit_activated: boolean;
  complete_status: 'INCOMPLETE' | 'COMPLETE';
  security_deposit_amount: string;
  security_deposit: null | SecurityDeposit;
  payment_refund: null | PaymentRefund;
  reservation_payments: ReservationPayment[];
  total_amount_to_pay: string;
  have_taxes_been_paid: boolean;
  have_payments_been_paid: boolean;
  checkin_online_emails_sent: number;
  housing: any;
  check_in_date: string;
  source_name: string;
  check_out_date: string;
  aggregated_status: string;
  aggregated_payments_status: AGGREGATED_RESERVATION_PAYMENTS_STATUSES;
  assigned_to: string;
  created_at: string;
  currency: string;
  default_invite_email: string;
  default_leader_full_name: string;
  deposit: null;
  external_id: string;
  external_room_id: string;
  fee: null;
  guest_group: GuestGroup;
  id: string;
  max_nights_taxable: number | null;
  nights_exempted: number | null;
  nights_of_stay: number;
  nights_taxable: number | null;
  occupied_rooms_quantity: number;
  origin: string;
  people_exempted: number | null;
  people_taxable: number | null;
  price: number | null;
  should_be_paid_on_chekin_online: boolean;
  signup_form_link: string;
  status: string;
  status_display: string;
  status_invite_email_sending: string | null;
  status_invite_email_sending_display: string | null;
  tax: number | null;
  total_nights: number | null;
  all_guests_passed_biomatch: string;
  id_verification_retry_link?: string;
  manuallyUpdated?: boolean;
  tax_activated?: boolean;
};

export type Supplier = {
  id: string;
  name: string;
  email: string;
  phone_number: string;
  address: string;
  phone_number_details: {code: string; number: string};
};

export enum DAY_NAMES {
  monday = 'MON',
  tuesday = 'TUE',
  wednesday = 'WED',
  thursday = 'THU',
  friday = 'FRI',
  saturday = 'SAT',
  sunday = 'SUN',
}

export type ShareSharedAction = {
  action: 'sharedAction';
  activityType?: string;
};

export type ShareDismissedAction = {
  action: 'dismissedAction';
};

export type ShareAction = ShareSharedAction | ShareDismissedAction;

export type ShareContent =
  | {
      title?: string;
      message: string;
    }
  | {
      title?: string;
      url: string;
    };

export type ShareOptions = {
  dialogTitle?: string;
  excludedActivityTypes?: Array<string>;
  tintColor?: string;
  subject?: string;
};
export interface ShareStatic {
  share(content: ShareContent, options?: ShareOptions): Promise<ShareAction>;
  sharedAction: 'sharedAction';
  dismissedAction: 'dismissedAction';
}

export type GlobalLocationTypes = IdentityLocationStateType &
  LocationStateTaxes & {
    formData: any;
    number_of_guests?: number;
    children?: number;
    ocrWasUsed?: boolean;
    backSideScan: string;
    frontSideScan?: string;
    customFormId?: string;
    signature?: string;
    biomatch?: {
      biomatch_passed: boolean;
      biomatch_doc: string;
      biomatch_selfie: string;
    };
  };

export type IconNames =
  | 'arrow-down'
  | 'arrow-bottom'
  | 'arrow-left'
  | 'arrow-right'
  | 'email'
  | 'comment'
  | 'home'
  | 'home-fill'
  | 'globe'
  | 'bell'
  | 'flag'
  | 'building'
  | 'arrow-next'
  | 'arrow-prev'
  | 'notification'
  | 'bulb'
  | 'coin'
  | 'star'
  | 'play'
  | 'modem'
  | 'burger'
  | 'check'
  | 'share'
  | 'more'
  | 'heart'
  | 'heart-fill'
  | 'close'
  | 'location'
  | 'image'
  | 'toilet-paper'
  | 'credit-card'
  | 'medical-case'
  | 'monitor'
  | 'calendar'
  | 'user'
  | 'plus'
  | 'minus'
  | 'bag'
  | 'star-outline'
  | 'instagram'
  | 'facebook'
  | 'twitter'
  | 'pinterest'
  | 'smile'
  | 'edit'
  | 'hand-cart'
  | 'wallet'
  | 'receipt'
  | 'close-circle'
  | 'close-circle-fill'
  | 'search'
  | 'plus-circle'
  | 'minus-circle'
  | 'upload-file'
  | 'pencil'
  | 'route'
  | 'tick'
  | 'lock'
  | 'marker'
  | 'car'
  | 'circle-and-square'
  | 'pen'
  | 'lightning'
  | 'stopwatch'
  | 'clock'
  | 'candlesticks'
  | 'glass'
  | 'at'
  | 'pizza-slice'
  | 'google'
  | 'apple'
  | 'eye';
